import {
    useBlockProps,
    InspectorControls,
    BlockControls,
    BlockAlignmentToolbar,
} from "@wordpress/block-editor";
import {
    PanelBody,
    SelectControl,
    TextControl,
    TextareaControl,
    Button,
    ButtonGroup,
    Disabled,
    Spinner,
    Notice,
    ToolbarGroup,
    ToolbarDropdownMenu,
} from "@wordpress/components";
import DebouncedServerSideRender from "../../../shared/js/DebouncedServerSideRender";
import HydratedSourceControl from "../../../shared/js/HydratedSourceControl";
import useLinkedProfile from "../../../shared/js/useLinkedProfile";
import { __, sprintf } from "@wordpress/i18n";

const HEADING_TAG_OPTIONS = ["h1", "h2", "h3", "h4", "div"];

const VARIANT_OPTIONS = [
    { label: __("Default", "content-egg"), value: "default" },
    { label: __("Highlight", "content-egg"), value: "highlight" },
    { label: __("Inline", "content-egg"), value: "inline" },
    { label: __("Split", "content-egg"), value: "split" },
];

const DATA_SOURCE_OPTIONS = [
    { value: "auto",   label: __("Auto-fill from linked profile", "content-egg") },
    { value: "manual", label: __("Manual override (custom URLs)", "content-egg") },
];

export default function Edit({ attributes, setAttributes }) {
    const blockProps = useBlockProps({
        className: attributes.align ? `align${attributes.align}` : "",
    });

    const {
        variant,
        heading_tag,
        color_scheme,
        data_source,
        eyebrow,
        headline,
        text,
        primary_label,
        primary_url,
        primary_url_source,
        primary_meta,
        secondary_label,
        secondary_url,
        secondary_url_source,
        context,
        logo,
    } = attributes;

    const { loading: profileLoading, summary: linkedProfile } = useLinkedProfile();

    // Effective mode: auto only if a profile is actually linked AND user picked auto.
    const useSource = !!linkedProfile && data_source !== "manual";

    // Derive option lists for the source dropdowns.
    const urlOptions = (linkedProfile?.available_url_keys ?? []).map((entry) => ({
        value: entry.key,
        label: entry.label,
    }));

    const hasData = !!(headline || primary_label);

    return (
        <>
            <BlockControls>
                <ToolbarGroup>
                    <ToolbarDropdownMenu
                        icon="screenoptions"
                        label={__("Variant", "content-egg")}
                        text={VARIANT_OPTIONS.find((o) => o.value === variant)?.label}
                        controls={VARIANT_OPTIONS.map((option) => ({
                            title: option.label,
                            isActive: variant === option.value,
                            onClick: () => setAttributes({ variant: option.value }),
                        }))}
                    />
                </ToolbarGroup>
                <BlockAlignmentToolbar
                    value={attributes.align}
                    onChange={(val) => setAttributes({ align: val })}
                    controls={["wide", "full"]}
                />
            </BlockControls>

            <InspectorControls>
                <PanelBody title={__("Layout", "content-egg")} initialOpen={true}>
                    <SelectControl
                        label={__("Variant", "content-egg")}
                        value={variant}
                        options={VARIANT_OPTIONS}
                        onChange={(val) => setAttributes({ variant: val })}
                        __nextHasNoMarginBottom
                    />
                    <SelectControl
                        label={__("Color Scheme", "content-egg")}
                        value={color_scheme}
                        options={[
                            { label: __("Auto (site default)", "content-egg"), value: "auto" },
                            { label: __("Light", "content-egg"), value: "light" },
                            { label: __("Dark", "content-egg"), value: "dark" },
                        ]}
                        onChange={(val) => setAttributes({ color_scheme: val })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                {profileLoading && (
                    <PanelBody title={__("Data source", "content-egg")} initialOpen={false}>
                        <Spinner />
                    </PanelBody>
                )}

                {!profileLoading && linkedProfile && (
                    <PanelBody title={__("Data source", "content-egg")} initialOpen={true}>
                        <p style={{ fontSize: "12px", color: "#555", margin: "0 0 8px" }}>
                            {sprintf(
                                /* translators: %s: linked profile name */
                                __("Linked profile: %s", "content-egg"),
                                linkedProfile.profile_name
                            )}
                        </p>
                        <SelectControl
                            label={__("URL & logo source", "content-egg")}
                            help={__(
                                "Auto-fill pulls URLs and logo from the linked profile at render time. Switch to manual to enter custom values for this block only.",
                                "content-egg"
                            )}
                            value={data_source || "auto"}
                            options={DATA_SOURCE_OPTIONS}
                            onChange={(val) => setAttributes({ data_source: val })}
                            __nextHasNoMarginBottom
                        />
                    </PanelBody>
                )}

                <PanelBody title={__("Content", "content-egg")} initialOpen={true}>
                    <TextControl
                        label={__("Eyebrow", "content-egg")}
                        help={__("Small section label above the headline.", "content-egg")}
                        value={eyebrow}
                        onChange={(val) => setAttributes({ eyebrow: val })}
                        __nextHasNoMarginBottom
                    />
                    <TextControl
                        label={__("Headline", "content-egg")}
                        value={headline}
                        onChange={(val) => setAttributes({ headline: val })}
                        __nextHasNoMarginBottom
                    />
                    <div style={{ marginBottom: "8px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 500, marginBottom: "6px" }}>
                            {__("Heading Tag", "content-egg")}
                        </div>
                        <ButtonGroup>
                            {HEADING_TAG_OPTIONS.map((tag) => (
                                <Button
                                    key={tag}
                                    variant={heading_tag === tag ? "primary" : "secondary"}
                                    size="small"
                                    onClick={() => setAttributes({ heading_tag: tag })}
                                >
                                    {tag.toUpperCase()}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </div>
                    <TextareaControl
                        label={__("Body text", "content-egg")}
                        value={text}
                        onChange={(val) => setAttributes({ text: val })}
                        __nextHasNoMarginBottom
                    />
                    {useSource && linkedProfile.has_logo ? (
                        <Notice status="info" isDismissible={false}>
                            {sprintf(
                                /* translators: %s: linked profile name */
                                __("Logo will be hydrated from %s on save.", "content-egg"),
                                linkedProfile.profile_name
                            )}
                        </Notice>
                    ) : (
                        <TextControl
                            label={__("Logo URL", "content-egg")}
                            help={__("Used by the Highlight variant only.", "content-egg")}
                            value={logo}
                            onChange={(val) => setAttributes({ logo: val })}
                            __nextHasNoMarginBottom
                        />
                    )}
                </PanelBody>

                <PanelBody title={__("Primary action", "content-egg")} initialOpen={true}>
                    <TextControl
                        label={__("Label", "content-egg")}
                        value={primary_label}
                        onChange={(val) => setAttributes({ primary_label: val })}
                        __nextHasNoMarginBottom
                    />
                    <HydratedSourceControl
                        label={__("URL", "content-egg")}
                        useSource={useSource}
                        sourceValue={primary_url_source}
                        literalValue={primary_url}
                        options={urlOptions}
                        onSourceChange={(val) => setAttributes({ primary_url_source: val })}
                        onLiteralChange={(val) => setAttributes({ primary_url: val })}
                        noOptionsNotice={__("The linked profile has no URLs set.", "content-egg")}
                    />
                    <TextControl
                        label={__("Meta", "content-egg")}
                        help={__("Trust/context line at the point of action, e.g. '★ 4.8 · 9,400+ teams'.", "content-egg")}
                        value={primary_meta}
                        onChange={(val) => setAttributes({ primary_meta: val })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                <PanelBody title={__("Secondary action", "content-egg")} initialOpen={false}>
                    <TextControl
                        label={__("Label", "content-egg")}
                        value={secondary_label}
                        onChange={(val) => setAttributes({ secondary_label: val })}
                        __nextHasNoMarginBottom
                    />
                    <HydratedSourceControl
                        label={__("URL", "content-egg")}
                        useSource={useSource}
                        sourceValue={secondary_url_source}
                        literalValue={secondary_url}
                        options={urlOptions}
                        onSourceChange={(val) => setAttributes({ secondary_url_source: val })}
                        onLiteralChange={(val) => setAttributes({ secondary_url: val })}
                        noOptionsNotice={__("The linked profile has no URLs set.", "content-egg")}
                    />
                </PanelBody>

                <PanelBody title={__("Context / footnote", "content-egg")} initialOpen={false}>
                    <TextareaControl
                        label={__("Context", "content-egg")}
                        help={__("Disclosure or footnote line. Not rendered in the Inline variant.", "content-egg")}
                        value={context}
                        onChange={(val) => setAttributes({ context: val })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>
            </InspectorControls>

            <div {...blockProps}>
                {hasData ? (
                    <Disabled>
                        <DebouncedServerSideRender
                            block="eggb/contextual-cta"
                            attributes={attributes}
                        />
                    </Disabled>
                ) : (
                    <p style={{ color: "#999", fontStyle: "italic", padding: "16px", margin: 0, fontSize: "13px" }}>
                        {__("Add a headline or primary action label in the sidebar.", "content-egg")}
                    </p>
                )}
            </div>
        </>
    );
}
